import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { FileText, Download } from "lucide-react";
import { CLIENT_NAV } from "@/lib/client-nav";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { useClientTier } from "@/hooks/useClientTier";
import { CommandCenterBanner } from "@/components/client/CommandCenterBanner";
import { CommandCenterTeaser } from "@/components/client/CommandCenterTeaser";
import { DailyFamilyMoment } from "@/components/client/DailyFamilyMoment";
import { UpcomingReviewCard } from "@/components/client/UpcomingReviewCard";
import { CareerInviteCard } from "@/components/conversion/CareerInviteCard";
import { ClientOnboardingJourney } from "@/components/client/ClientOnboardingJourney";
import {
  MessageSquare,
  ListChecks,
  Users as UsersIcon,
  Target,
  Calendar as CalendarIcon,
  ShieldAlert,
  Share2,
  FileBarChart2,
  Upload,
  CalendarCheck,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/client")({
  head: () => ({
    meta: [
      { title: "Client Portal — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ClientPortal,
});

interface Stats {
  activePolicies: number;
  totalCoverage: number;
  nextReview: string | null;
  onboardingStep: number;
  onboardingStatus: string;
  clientCreatedAt: string | null;
  agent: { full_name: string | null; license_states: string[] | null; avatar_url: string | null } | null;
  documents: Array<{ id: string; name: string; created_at: string; mime_type: string | null }>;
}

interface LibraryItem {
  id: string;
  title: string;
  slug: string | null;
  thumbnail_url: string | null;
  product_type: string | null;
}

interface LibraryState {
  mode: "owned" | "recommended";
  items: LibraryItem[];
}

function StatSeal({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-cream-50 border border-bronze-600/15 rounded-[4px] p-6 shadow-soft flex flex-col items-center text-center">
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        {label}
      </p>
      <p className="mt-3 font-display font-semibold text-navy-800 text-[32px] leading-none">
        {value}
      </p>
    </div>
  );
}

function ClientPortal() {
  const location = useLocation();
  const isOverview = location.pathname === "/client";

  return (
    <ProtectedRoute requiredRole="client">
      {isOverview ? (
        <PortalShell title="Client Portal" nav={CLIENT_NAV} tourKey="client_tour" headerVariant="cream">
          <ClientDashboard />
        </PortalShell>
      ) : (
        <Outlet />
      )}
    </ProtectedRoute>
  );
}

function ClientDashboard() {
  const { user, profile } = useAuth();
  const { isLegacy, isFamily } = useClientTier();
  const [stats, setStats] = useState<Stats | null>(null);
  const [library, setLibrary] = useState<LibraryState | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: client } = await supabase
        .from("clients")
        .select("id, assigned_agent_id, onboarding_step, onboarding_status, created_at")
        .eq("user_id", user.id)
        .maybeSingle();

      const clientId = client?.id;

      const [policiesRes, apptRes, docsRes, agentRes] = await Promise.all([
        clientId
          ? supabase.from("policies").select("status, face_amount").eq("client_id", clientId)
          : Promise.resolve({ data: [] as Array<{ status: string; face_amount: number | null }> }),
        clientId
          ? supabase
              .from("appointments")
              .select("scheduled_at")
              .eq("client_id", clientId)
              .gte("scheduled_at", new Date().toISOString())
              .order("scheduled_at", { ascending: true })
              .limit(1)
          : Promise.resolve({ data: [] as Array<{ scheduled_at: string }> }),
        supabase
          .from("documents")
          .select("id, name, created_at, mime_type")
          .eq("owner_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5),
        client?.assigned_agent_id
          ? supabase
              .from("profiles")
              .select("full_name, avatar_url")
              .eq("id", client.assigned_agent_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      let agentLicenseStates: string[] | null = null;
      if (client?.assigned_agent_id) {
        const { data: agentRow } = await supabase
          .from("agents")
          .select("license_states")
          .eq("user_id", client.assigned_agent_id)
          .maybeSingle();
        agentLicenseStates = (agentRow?.license_states as string[] | null) ?? null;
      }

      const policies = (policiesRes.data ?? []) as Array<{ status: string; face_amount: number | null }>;
      const active = policies.filter((p) => p.status === "active");
      const totalCov = active.reduce((s, p) => s + (Number(p.face_amount) || 0), 0);
      const next = apptRes.data?.[0]?.scheduled_at ?? null;
      const agentProfile =
        (agentRes.data as { full_name: string | null; avatar_url: string | null } | null) ?? null;

      setStats({
        activePolicies: active.length,
        totalCoverage: totalCov,
        nextReview: next,
        onboardingStep: client?.onboarding_step ?? 0,
        onboardingStatus: client?.onboarding_status ?? "in_progress",
        clientCreatedAt: (client as any)?.created_at ?? null,
        agent: agentProfile
          ? {
              full_name: agentProfile.full_name,
              avatar_url: agentProfile.avatar_url,
              license_states: agentLicenseStates,
            }
          : null,
        documents: (docsRes.data ?? []) as Stats["documents"],
      });
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: owned } = await supabase
        .from("user_library")
        .select("id, granted_at, product:products(id, title, slug, thumbnail_url, product_type)")
        .eq("user_id", user.id)
        .order("granted_at", { ascending: false })
        .limit(3);
      const ownedItems = (owned ?? [])
        .map((r) => r.product as LibraryItem | null)
        .filter((p): p is LibraryItem => Boolean(p));
      if (ownedItems.length > 0) {
        setLibrary({ mode: "owned", items: ownedItems });
        return;
      }
      const { data: recs } = await supabase
        .from("products")
        .select("id, title, slug, thumbnail_url, product_type")
        .eq("published", true)
        .eq("featured", true)
        .order("created_at", { ascending: false })
        .limit(3);
      setLibrary({ mode: "recommended", items: (recs ?? []) as LibraryItem[] });
    })();
  }, [user]);

  const firstName = (profile?.full_name || "").split(" ")[0] || "Friend";
  const step = stats?.onboardingStep ?? 0;
  const progressPct = Math.min(100, (step / 5) * 100);

  const fmtCurrency = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="max-w-6xl mx-auto">
      {isLegacy && (
        <CommandCenterBanner
          firstName={firstName}
          familyCount={null}
          goalsOnTrack={0}
        />
      )}

      <p
        className="font-display italic text-bronze-600"
        style={{ fontSize: 18 }}
      >
        Your family's plan, at a glance.
      </p>
      <h2
        className="mt-2 font-display font-semibold text-navy-800"
        style={{ fontSize: "36px", letterSpacing: "-0.01em" }}
      >
        Welcome back, <em className="text-bronze-600 italic font-normal">{firstName}</em>
      </h2>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatSeal label="Active Policies" value={String(stats?.activePolicies ?? 0)} />
        <StatSeal label="Total Coverage" value={fmtCurrency(stats?.totalCoverage ?? 0)} />
        <StatSeal
          label="Next Review"
          value={stats?.nextReview ? fmtDate(stats.nextReview) : "Not scheduled"}
        />
        <StatSeal label="Onboarding" value={`${Math.round(progressPct)}%`} />
      </div>

      <DailyFamilyMoment />

      <UpcomingReviewCard />

      <div data-tour="client-checklist">
        <ClientOnboardingJourney />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-tour="client-strategist" className="lg:col-span-2 bg-white rounded-[4px] p-6 border border-bronze-600/20 shadow-soft">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            Your Strategist
          </p>
          <div className="mt-4 flex items-center gap-5">
            {stats?.agent?.avatar_url ? (
              <img
                src={stats.agent.avatar_url}
                alt={stats.agent.full_name ?? "Strategist"}
                className="h-20 w-20 rounded-full object-cover border-2 border-bronze-600/40"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-navy-800 text-cream-50 font-display text-[28px] flex items-center justify-center border-2 border-bronze-600/40">
                {(stats?.agent?.full_name || "A")[0]}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-display font-semibold text-navy-800 text-[20px]">
                {stats?.agent?.full_name || "Not yet assigned"}
              </p>
              <p className="font-sans text-[13px] text-charcoal-500">
                Licensed Strategist
              </p>
              {stats?.agent?.license_states && stats.agent.license_states.length > 0 && (
                <p className="mt-1 font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600">
                  Licensed in {stats.agent.license_states.join(", ")}
                </p>
              )}
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <PrimaryButton className="bg-navy-800 text-cream-50 hover:bg-navy-700">
              Message
            </PrimaryButton>
            <PrimaryButton>Book Review</PrimaryButton>
          </div>
        </div>

        <div className="bg-white rounded-[4px] p-6 border border-bronze-600/20 shadow-soft">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            Recent Documents
          </p>
          <ul className="mt-4 divide-y divide-bronze-600/15">
            {(stats?.documents ?? []).length === 0 && (
              <li className="py-6 text-center font-sans text-[13px] text-charcoal-500">
                No documents yet.
              </li>
            )}
            {(stats?.documents ?? []).map((d) => (
              <li key={d.id} className="py-3 flex items-center gap-3">
                <FileText size={16} className="text-bronze-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-[13px] text-charcoal-900 truncate">{d.name}</p>
                  <p className="font-sans text-[11px] text-charcoal-500">
                    {fmtDate(d.created_at)}
                  </p>
                </div>
                <span className="px-2 py-0.5 bg-bronze-600/15 text-bronze-600 font-sans text-[10px] uppercase tracking-[0.12em] rounded-full">
                  Doc
                </span>
                <Download size={15} className="text-charcoal-500 hover:text-navy-800 cursor-pointer" />
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-[4px] p-6 border border-bronze-600/20 shadow-soft flex flex-col">
          <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
            From the Store
          </p>
          <ul className="mt-4 flex-1 divide-y divide-bronze-600/15">
            {!library && (
              <li className="py-6 text-center font-sans text-[13px] text-charcoal-500">
                Loading…
              </li>
            )}
            {library && library.items.length === 0 && (
              <li className="py-6 text-center font-sans text-[13px] text-charcoal-500">
                Nothing here yet.
              </li>
            )}
            {library?.items.map((p) => (
              <li key={p.id} className="py-3 flex items-center gap-3">
                {p.thumbnail_url ? (
                  <img
                    src={p.thumbnail_url}
                    alt=""
                    className="h-12 w-12 rounded-sm object-cover shrink-0"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-sm bg-navy-900/5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-[13px] text-charcoal-900 truncate">{p.title}</p>
                  {p.product_type && (
                    <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
                      {p.product_type.replace(/-/g, " ")}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {library && (
            <Link
              to={library.mode === "owned" ? "/store/library" : "/store"}
              className="mt-5 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600 hover:text-navy-800"
            >
              {library.mode === "owned"
                ? "View all in your Library →"
                : "Browse the Store →"}
            </Link>
          )}
        </div>
      </div>

      {isLegacy && <CommandCenterGrid />}
      {isFamily && <CommandCenterTeaser />}

      {stats?.clientCreatedAt && stats.activePolicies > 0 &&
        (Date.now() - new Date(stats.clientCreatedAt).getTime()) < 60 * 24 * 60 * 60 * 1000 && (
          <CareerInviteCard source="dashboard" />
      )}

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Message Strategist", href: "/client/messages" as const, icon: MessageSquare },
          { label: "Upload Document", href: "/client/vault" as const, icon: Upload },
          { label: "Take Family Snapshot", href: "/tools/family-snapshot" as const, icon: Sparkles },
          { label: "Book Review", href: "/book" as const, icon: CalendarCheck },
        ].map((a) => {
          const Icon = a.icon;
          return (
            <Link
              key={a.label}
              to={a.href}
              className="flex items-center justify-center gap-2 text-center py-3 border border-bronze-600 text-bronze-600 hover:bg-bronze-600 hover:text-navy-900 font-sans text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors rounded-[2px]"
            >
              <Icon size={14} />
              {a.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

const COMMAND_CENTER_LINKS = [
  { label: "Secure Messages", href: "/client/messages", icon: MessageSquare },
  { label: "Planning Checklist", href: "/client/checklist", icon: ListChecks },
  { label: "Family Members", href: "/client/family", icon: UsersIcon },
  { label: "Goals Tracker", href: "/client/goals", icon: Target },
  { label: "Annual Review", href: "/client", icon: CalendarIcon },
  { label: "Beneficiary Audit", href: "/client", icon: ShieldAlert },
  { label: "Estate Sharing", href: "/client/vault/share", icon: Share2 },
  { label: "Annual Statement", href: "/client/annual-statement", icon: FileBarChart2 },
] as const;

function CommandCenterGrid() {
  return (
    <div className="mt-10">
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        Command Center
      </p>
      <h3 className="mt-2 font-display font-semibold text-navy-800 text-[22px]">
        The work of your household.
      </h3>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {COMMAND_CENTER_LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <Link
              key={l.label}
              to={l.href as any}
              className="group bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft hover:border-bronze-600/60 hover:-translate-y-0.5 transition-all"
            >
              <div className="h-9 w-9 rounded-full bg-bronze-600/15 flex items-center justify-center">
                <Icon size={16} className="text-bronze-600" />
              </div>
              <p className="mt-3 font-sans text-[13px] font-semibold text-navy-800">
                {l.label}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}